import type { FC } from 'react';

import {
  httpGetTeams,
  httpGetTeamsBasic,
} from '@iconik-io/iconik-api/src/services/users';

import UserNameValue from '../../../../components/select_field_custom_values/select_field_username_value/select_field_username_value';
import { useAppDispatch } from '../../../../store/react_redux_typed';
import type {
  SelectGroupsMultiProps,
  SelectGroupsProps,
} from '../../../../types/select_groups';
import { mapOptions } from '../../../../utils/map_options';
import { prepareQueryParameter } from '../../../../utils/search_utils/search_utils';
import type { SelectOption } from '../../../select_field/types';
import {
  type LoadOptionsResult,
  resolveRemoteResponse,
} from '../../../select_field/utils';
import SelectFieldWrapper from '../select/select';
import SelectFieldMultiWrapper from '../select_multi/select_multi';

type SelectTeamsFieldWrapperProps = (
  | SelectGroupsProps
  | SelectGroupsMultiProps
) & {
  additionalOptions?: SelectOption[];
};

export const SelectTeamsFieldWrapper: FC<SelectTeamsFieldWrapperProps> = ({
  additionalOptions = [],
  ...props
}) => {
  const dispatch = useAppDispatch();
  const handleLoadOptions = (query: string): Promise<LoadOptionsResult> => {
    const { loadFullGroups } = props;
    const params = {
      query: prepareQueryParameter(query),
      per_page: 100,
    };

    if (loadFullGroups) {
      return dispatch(httpGetTeams(params)).then(
        resolveRemoteResponse((response) => [
          ...response.body.objects.map(mapOptions),
          ...additionalOptions,
        ]),
      );
    }
    return dispatch(httpGetTeamsBasic(params)).then(
      resolveRemoteResponse((response) => [
        ...response.body.objects.map(mapOptions),
        ...additionalOptions,
      ]),
    );
  };

  const handleLoadSelectedOptions = (
    ids: string[],
  ): Promise<SelectOption[]> => {
    const { loadFullGroups } = props;
    const additionalMatches = additionalOptions.filter((o) =>
      ids.includes(o.value),
    );
    const remainingIds = ids.filter(
      (id) => !additionalMatches.some((o) => o.value === id),
    );

    if (!remainingIds.length) {
      return Promise.resolve(additionalMatches);
    }

    const params = {
      ids: remainingIds.join(','),
      per_page: 100,
    };

    if (loadFullGroups) {
      return dispatch(httpGetTeams(params)).then((response) => [
        ...response.body.objects.map(mapOptions),
        ...additionalMatches,
      ]);
    }
    return dispatch(httpGetTeamsBasic(params)).then((response) => [
      ...response.body.objects.map(mapOptions),
      ...additionalMatches,
    ]);
  };

  const sharedProps = {
    preloadOptions: true,
    valueComponent: props.valueComponent ?? UserNameValue,
    loadOptions: handleLoadOptions,
    loadSelectedOptions: handleLoadSelectedOptions,
  };

  if (props.isMulti) {
    return <SelectFieldMultiWrapper {...props} {...sharedProps} />;
  }

  return <SelectFieldWrapper {...props} {...sharedProps} />;
};

export default SelectTeamsFieldWrapper;
